import React, { useEffect, useRef } from 'react';

export default function ServiceVideo({ video, label, aspect = 'portrait' }) {
  const ref = useRef(null);

  // iOS Safari needs an explicit play() call after mount for autoplay in some cases.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    // Trigger once ready, and also on visibility change
    tryPlay();
    el.addEventListener('canplay', tryPlay, { once: true });
    // Kick playback when scrolled into view (mobile)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) tryPlay();
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => {
      el.removeEventListener('canplay', tryPlay);
      io.disconnect();
    };
  }, []);

  const cls =
    aspect === 'portrait'
      ? 'aspect-[3/4] max-w-md'
      : 'aspect-video w-full';
  return (
    <div className={`relative rounded-3xl overflow-hidden border border-blue-500/12 bg-[#ffffff] ${cls}`}>
      <video
        ref={ref}
        autoPlay
        loop
        muted
        defaultMuted
        playsInline
        webkit-playsinline="true"
        preload="auto"
        disableRemotePlayback
        className="absolute inset-0 w-full h-full object-cover"
        aria-hidden="true"
      >
        {video.webm && <source src={video.webm} type="video/webm" />}
        {video.mp4 && <source src={video.mp4} type="video/mp4" />}
        {/* legacy single-src support */}
        {!video.webm && !video.mp4 && video.src && (
          <source src={video.src} type="video/mp4" />
        )}
      </video>
      {/* Minimal overlay — video stays clearly visible */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#ffffff] via-[#ffffff]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-500/12 mix-blend-overlay" />
      {/* Corner glow */}
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-blue-500/12 blur-2xl" />
      {/* Label */}
      {label && (
        <div className="absolute inset-x-5 bottom-5 flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-blue-600">LEADEVEREST</div>
            <div className="font-display text-lg font-medium text-slate-900">{label}</div>
          </div>
          <span className="h-2 w-2 rounded-full bg-blue-500 shadow-[0_0_18px_rgba(37,99,235,0.9)]" />
        </div>
      )}
      {/* Corner mark with pill for contrast */}
      <div className="absolute top-4 left-4 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 font-mono text-[10px] tracking-widest text-blue-600 shadow-sm">▍ LIVE</div>
    </div>
  );
}
